/**
 * 평가지(인쇄용) 소스코드 블럭 HTML 정적 렌더용 정리기.
 * - 스크립트·iframe·폼·이벤트 핸들러·외부 리소스 제거 (DOMPurify)
 * - <style> 태그 불허 → 인라인 style 속성만 사용 (다른 블럭/앱 CSS 오염 방지)
 * - 이미지·링크는 data:image/* 만 허용, style 안의 url(...)도 data:image 만 허용
 * - position:fixed/sticky 는 absolute 로 낮춤 (블럭 밖으로 튀어나가지 않게)
 * 프레젠테이션 재생 모드의 iframe 실행과는 무관합니다.
 */
import DOMPurify from 'dompurify';

const FORBID_TAGS = [
  'script', 'style', 'iframe', 'frame', 'frameset', 'object', 'embed', 'applet', 'form', 'input', 'button',
  'textarea', 'select', 'option', 'link', 'meta', 'base', 'noscript', 'template', 'video', 'audio', 'source',
  'track', 'canvas', 'dialog', 'portal', 'math',
];
const FORBID_ATTR = ['srcset', 'action', 'formaction', 'ping', 'background', 'poster', 'xlink:href', 'target'];

/**
 * ALLOWED_URI_REGEXP 를 data:image 로 좁혔기 때문에, DOMPurify 는 "URI 안전" 목록에 없는 속성 값이
 * 이 정규식에 안 맞으면 지웁니다(예: SVG 의 x, width, fill, viewBox / 표의 colspan).
 * URL 이 될 수 없는 도형·표 속성만 명시적으로 안전 목록에 추가합니다.
 */
const SAFE_PRESENTATION_ATTR = [
  'viewbox', 'preserveaspectratio', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry', 'dx', 'dy',
  'width', 'height', 'd', 'points', 'transform', 'fill', 'fill-opacity', 'fill-rule', 'stroke', 'stroke-width',
  'stroke-dasharray', 'stroke-linecap', 'stroke-linejoin', 'stroke-opacity', 'opacity', 'text-anchor',
  'dominant-baseline', 'font-size', 'font-weight', 'font-family', 'font-style', 'offset', 'stop-color',
  'stop-opacity', 'marker-start', 'marker-mid', 'marker-end', 'markerwidth', 'markerheight', 'refx', 'refy',
  'orient', 'colspan', 'rowspan', 'align', 'valign', 'start', 'type', 'reversed',
];

let hooked = false;
function ensureHooks(): void {
  if (hooked) return;
  hooked = true;
  DOMPurify.addHook('uponSanitizeAttribute', (_node, data) => {
    if (data.attrName === 'style') {
      let v = String(data.attrValue || '');
      // data:image 가 아닌 url(...) 제거
      v = v.replace(/url\(\s*(['"]?)(.*?)\1\s*\)/gi, (m, _q, inner: string) =>
        /^data:image\//i.test(String(inner).trim()) ? m : 'none'
      );
      v = v.replace(/expression\s*\(/gi, '').replace(/@import/gi, '');
      v = v.replace(/position\s*:\s*(fixed|sticky)/gi, 'position:absolute');
      data.attrValue = v;
    }
    if ((data.attrName === 'href' || data.attrName === 'src') && !/^data:image\//i.test(String(data.attrValue || '').trim())) {
      data.keepAttr = false;
    }
  });
}

export function sanitizeSheetCodeHtml(raw: string): string {
  const src = String(raw ?? '');
  if (!src.trim()) return '';
  ensureHooks();
  return DOMPurify.sanitize(src, {
    FORBID_TAGS,
    FORBID_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOWED_URI_REGEXP: /^data:image\//i,
    ADD_URI_SAFE_ATTR: SAFE_PRESENTATION_ATTR,
    WHOLE_DOCUMENT: false,
    RETURN_TRUSTED_TYPE: false,
  }) as unknown as string;
}

/** 평가지 캡처 기준 폭(px) — evaluationPlanSheetCanvasDocumentExport 와 동일 */
export const SHEET_CODE_DESIGN_PAGE_WIDTH_PX = 794;
